import { getSearchWith } from "@/utils/searchHelper"
import { useCallback, useMemo } from "react"
import { useSearchParams } from "react-router-dom"

type Value = string | boolean | string[] | number
type Config = Record<string, Value>

type UseFilterParams = <T extends Config>(
  defaultFilters: T
) => {
  filters: T
  setFilters: (newFilters: Partial<T>, resetPagination?: boolean) => void
  filtersKey: string
}

export const useFilterParams: UseFilterParams = <T extends Config>(defaultFilters: T) => {
  const [searchParams, setSearchParams] = useSearchParams()

  const filters = Object.keys(defaultFilters).reduce(
    (acc, key) => {
      const defaultValue = defaultFilters[key]

      if (Array.isArray(defaultValue)) {
        const values = searchParams.getAll(key)
        acc[key] = values.length > 0 ? values : defaultValue
        return acc
      }

      const value = searchParams.get(key)
      if (value === null) {
        acc[key] = defaultValue
      } else if (typeof defaultValue === "boolean") {
        acc[key] = value === "true"
      } else {
        acc[key] = value
      }

      return acc
    },
    {} as Record<string, Value>
  ) as T

  const setFilters = useCallback(
    (newFilters: Partial<T>, resetPagination = true) => {
      const params: Record<string, string | string[] | null> = resetPagination ? { page: "1", pageSize: "10" } : {}

      Object.keys(defaultFilters).forEach(key => {
        const value = key in newFilters ? newFilters[key] : filters[key]
        const defaultValue = defaultFilters[key]

        if (Array.isArray(defaultValue)) {
          const currentArray = Array.isArray(value) ? value : []
          const isDefault = JSON.stringify(currentArray) === JSON.stringify(defaultValue)
          params[key] = isDefault || currentArray.length === 0 ? null : currentArray
        } else {
          params[key] = value === defaultValue ? null : String(value)
        }
      })

      setSearchParams(getSearchWith(searchParams, params))
    },
    [searchParams, setSearchParams, defaultFilters, filters]
  )

  const filtersKey = useMemo(() => {
    const relevantParams = new URLSearchParams()

    Object.keys(defaultFilters).forEach(key => {
      const values = searchParams.getAll(key)
      values.forEach(value => {
        relevantParams.append(key, value)
      })
    })

    relevantParams.sort()

    return relevantParams.toString()
  }, [defaultFilters, searchParams])

  return { filters, setFilters, filtersKey }
}
